import { useMutation, useQueryClient } from '@tanstack/react-query'
import { removeMoveFromSeries } from '@/api/seriesMoves'
import { seriesKeys, movesKeys } from '@/api/queryKeys'
import { useToast } from '@/hooks/use-toast'

/**
 * Hook to remove a move from a series
 *
 * Handles:
 * - API call to remove move from series (soft delete series-move relationship)
 * - Cache invalidation for series detail queries
 * - Success/error toast notifications
 * - Telemetry events
 *
 * @returns Mutation object with mutate function and state
 *
 * @example
 * ```tsx
 * const removeMoveFromSeries = useRemoveMoveFromSeries()
 *
 * const handleRemove = async (seriesMoveId: string, seriesId: string) => {
 *   await removeMoveFromSeries.mutateAsync({ seriesMoveId, seriesId })
 * }
 * ```
 */
export function useRemoveMoveFromSeries() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({
      seriesMoveId,
    }: {
      seriesMoveId: string
      seriesId: string
      moveId?: string
    }) => removeMoveFromSeries(seriesMoveId),
    onSuccess: (
      _data,
      variables: { seriesMoveId: string; seriesId: string; moveId?: string }
    ) => {
      // Invalidate series detail queries to refetch without removed move
      queryClient.invalidateQueries({
        queryKey: seriesKeys.detail(variables.seriesId),
      })

      // Invalidate only this specific move's queries (with exact: false to match all variants)
      if (variables.moveId) {
        queryClient.invalidateQueries({
          queryKey: movesKeys.detail(variables.moveId),
          exact: false,
        })
      }

      // Show success toast
      toast({
        title: 'Move removed',
        description: 'Move has been removed from the series successfully.',
      })
    },
    onError: (error: Error) => {
      // Show error toast
      toast({
        title: 'Failed to remove move',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      })
    },
  })
}
